import { DeviceNodeCleanupService } from '@caslink/device-client/ng-app/shared/services/device-node-cleanup.service';
import { DeviceService } from './device.service';
import { IDeviceInformation } from '../models/device';
import { Injectable } from '@angular/core';
import { ProxyService } from '@caslink/common-client/ng-app/proxy/ProxyService';
import { ChangeCommandRequestStateService } from '@caslink/device-client/ng-app/shared/services/change-command-request-state.service';
import { Subject, from } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class CommandService {
    public static $name = 'commandService';
    // We have to close previous requests - if we keep them alive they will mess up with data
    public $destroyAllSubscriptions = new Subject<boolean>();
    public $data: Subject<IDeviceInformation> = new Subject();

    constructor(
        private deviceService: DeviceService,
        private proxyService: ProxyService,
        private changeCommandRequestStateService: ChangeCommandRequestStateService,
        private deviceNodeCleanupService: DeviceNodeCleanupService,
    ) {
    }

    invokeCommand(existingDeviceInfo: IDeviceInformation, commandName: string) {
        if (!commandName) {
            return null;
        }

        this.$destroyAllSubscriptions.next(true);
        const entityId = existingDeviceInfo.$deviceHierarchy.identifier.entityId;
        this.changeCommandRequestStateService.startRequest(entityId);

        this.sendRequest(existingDeviceInfo, commandName)
            .pipe(
                takeUntil(this.$destroyAllSubscriptions),
            )
            .subscribe((node: IDeviceInformation) => {
                this.setupDeviceInfo(existingDeviceInfo, node);
                this.$data.next(node);
                this.$destroyAllSubscriptions.next();
                this.changeCommandRequestStateService.endRequest(entityId);
            });
    }

    private setupDeviceInfo(existingDeviceInfo: IDeviceInformation, node: IDeviceInformation) {
        const newDeviceInfo = this.deviceService.setupDeviceInfo(node) as IDeviceInformation;
        newDeviceInfo.$selectedCategory = existingDeviceInfo.$selectedCategory;
    }

    private invokeCommandByName(
        existingDeviceInfo: IDeviceInformation,
        commandName: string,
    ) {
        return this.proxyService.request({
            endPoint: 'hardware',
            route: commandName,
            data: this.deviceNodeCleanupService.cleanUpAndDeepCopy(existingDeviceInfo.$deviceHierarchy),
            routeParams: {
                hardwareItemId: CommandService.getHardwareItemIdFromDeviceInfo(existingDeviceInfo)
            }
        }).as<IDeviceInformation>();
    }

    private sendRequest(existingDeviceInfo: IDeviceInformation, commandName: string) {
        return from(this.invokeCommandByName(existingDeviceInfo, commandName));
    }

    // This will result in -1 rather than %7BhardwareItemId%7D in the URL parameter
    private static getHardwareItemIdFromDeviceInfo(deviceInfo: IDeviceInformation) {
        return deviceInfo?.$deviceHierarchy?.identifier?.entityId ?? -1;
    }
}
